/**
 * VerifyOwnership — the ladder's front door for buyers.
 *
 * Flow:
 *   1. If already a member of any project → status cards (verified / pending / rejected).
 *   2. New application wizard: pick project → units + proof upload (multi) → fast-lane → review → submit.
 *   3. Pending state shows queue info + fast-lane state; rejected shows reason + re-apply.
 *
 * Backend: /api/memberships/* (the canonical store).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Building2, Home, CheckCircle, Search, Loader2, Upload, X, FileText, Zap,
  ShieldCheck, Clock, XCircle, ArrowRight, ArrowLeft, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  verified:    { icon: ShieldCheck, color: 'text-green-600', bg: 'bg-green-50 border-green-200', label: 'Verified Member' },
  group_admin: { icon: ShieldCheck, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Group Admin' },
  pending:     { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Under Review' },
  rejected:    { icon: XCircle, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-200', label: 'Rejected' },
};

const HOLDER_OPTIONS = [
  ['1', 'First-named owner (primary)'], ['2', '2nd joint holder'], ['3', '3rd joint holder'], ['4', '4th joint holder'],
];
const HOLDER_SHORT = { 1: '1st-named owner', 2: '2nd joint holder', 3: '3rd joint holder', 4: '4th joint holder' };

export default function VerifyOwnership() {
  const { user, token } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [project, setProject] = useState(null);

  const [units, setUnits] = useState(['']);
  const [addingTo, setAddingTo] = useState(null);
  const [proofFiles, setProofFiles] = useState([]);       // [{name, url, uploading}]
  const [phone, setPhone] = useState(user?.phone || '');
  const [notes, setNotes] = useState('');
  const [holderPos, setHolderPos] = useState('1');
  const [fastLane, setFastLane] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const fileRef = useRef(null);

  const loadMemberships = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/api/memberships/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMemberships(res.data.memberships || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadMemberships(); }, [loadMemberships]);

  // Project search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`${API}/api/projects`, { params: { search: searchQuery } });
        setSearchResults(res.data || []);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const uploadProof = async (files) => {
    if (!files?.length) return;
    const list = Array.from(files);
    for (const f of list) {
      if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(f.type)) {
        toast.error(`${f.name}: only JPG, PNG, WebP or PDF`);
        continue;
      }
      const entry = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: f.name, url: null, uploading: true };
      setProofFiles((p) => [...p, entry]);
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/api/memberships/proof-upload`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProofFiles((p) => p.map((x) => (x.id === entry.id ? { ...x, url: res.data.url, uploading: false } : x)));
      } catch (e) {
        setProofFiles((p) => p.filter((x) => x.id !== entry.id));
        toast.error(e?.response?.data?.detail || `Upload failed for ${f.name}`);
      }
    }
  };

  const removeProof = (idx) => setProofFiles((p) => p.filter((_, i) => i !== idx));

  const [pendingUploadBusy, setPendingUploadBusy] = useState(null);
  const uploadPendingProof = async (m, files) => {
    if (!files?.length) return;
    setPendingUploadBusy(m.id);
    try {
      const urls = [];
      for (const f of Array.from(files)) {
        if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(f.type)) {
          toast.error(`${f.name}: only JPG, PNG, WebP or PDF`); continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API}/api/memberships/proof-upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
        urls.push(res.data.url);
      }
      if (urls.length) {
        await axios.post(`${API}/api/memberships/${m.id}/proof`, { proof_urls: urls }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Proof received — AI pre-check is running; our team reviews next.');
        loadMemberships();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Proof upload failed');
    } finally {
      setPendingUploadBusy(null);
    }
  };

  const addUnit = () => setUnits((u) => [...u, '']);
  const updateUnit = (i, v) => setUnits((u) => u.map((x, j) => (j === i ? v : x)));
  const removeUnit = (i) => setUnits((u) => u.filter((_, j) => j !== i));

  const cleanUnits = units.map((u) => u.trim()).filter(Boolean);
  const cleanProofs = proofFiles.filter((p) => p.url).map((p) => p.url);

  const canProceedStep2 = cleanUnits.length > 0 && !proofFiles.some((p) => p.uploading); // proof optional — can be uploaded later

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/memberships/apply`,
        {
          project_id: project.id,
          units: cleanUnits,
          proof_urls: cleanProofs,
          phone: phone || undefined,
          notes: notes || undefined,
          holder_position: Number(holderPos),
          developer_fast_lane: fastLane,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubmitted(res.data);
      toast.success(res.data?.message || 'Application submitted — under admin review');
      loadMemberships();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetWizard = () => {
    setStep(1); setProject(null); setSearchQuery(''); setUnits(['']);
    setProofFiles([]); setNotes(''); setFastLane(true); setSubmitted(null); setAddingTo(null); setHolderPos('1');
  };

  if (!user) {
    return (
      <Card className="max-w-lg mx-auto mt-10">
        <CardContent className="py-10 text-center space-y-3">
          <ShieldCheck className="h-10 w-10 mx-auto text-primary" />
          <p className="font-semibold">Sign in to verify your ownership</p>
          <p className="text-sm text-muted-foreground">
            Verification unlocks your project's Buyers' Group — Hub, Document Vault, Threads, Classifieds.
          </p>
          <Button onClick={() => window.dispatchEvent(new CustomEvent('fbc-auth', { detail: 'login' }))} data-testid="verify-signin-btn">Sign in or register</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="verify-ownership">
      <div>
        <h1 className="text-2xl font-bold">Ownership Verification</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Submit proof of purchase. Our team reviews every application; your developer can fast-confirm if you opt in.
        </p>
      </div>

      {/* Existing memberships */}
      {memberships.length > 0 && (
        <div className="space-y-3">
          {memberships.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <Card key={m.id} className={`border ${meta.bg}`} data-testid={`membership-card-${m.id}`}>
                <CardContent className="py-4 flex items-start gap-3">
                  <Icon className={`h-5 w-5 mt-0.5 ${meta.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{m.project_name || m.project_id}</p>
                      <Badge variant="secondary" className="text-[10px]">{meta.label}</Badge>
                      {m.developer_fast_lane_enabled && m.status === 'pending' && (
                        <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">
                          <Zap className="h-3 w-3 mr-1" /> Developer fast-lane active
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Units: {(m.units || []).join(', ') || '—'}
                      {' · '}
                      <select value={m.holder_position || ''} data-testid={`holder-position-${m.id}`}
                        onChange={async (e) => {
                          try {
                            await axios.patch(`${API}/api/memberships/${m.id}/holder-position`, { holder_position: Number(e.target.value) }, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Position on agreement updated'); loadMemberships();
                          } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
                        }}
                        className="bg-transparent border rounded px-1 py-0.5 text-xs text-foreground">
                        {!m.holder_position && <option value="">Set position on agreement…</option>}
                        {HOLDER_OPTIONS.map(([v]) => <option key={v} value={v}>{HOLDER_SHORT[v]}</option>)}
                      </select>
                      {' · '}Submitted{' '}
                      {m.created_at ? new Date(m.created_at).toLocaleDateString('en-IN') : '—'}
                    </p>
                    {m.status === 'pending' && (
                      <div className="mt-1.5 space-y-1.5">
                        <p className="text-xs text-muted-foreground">
                          {(m.proof_urls || []).length
                            ? "Our team is reviewing your proof. You'll be notified the moment you're approved."
                            : "No proof uploaded yet — you have full access meanwhile. Upload proof to complete verification."}
                          {m.developer_fast_lane_enabled && ' We\'ve also asked your developer to one-click confirm — whichever comes first unlocks you.'}
                        </p>
                        {m.proof_status === 'rejected' && (
                          <p className="text-xs text-rose-700" data-testid={`proof-rejected-${m.id}`}>{m.proof_rejection_reason || 'Proof did not pass review — please upload a clearer ownership document.'}</p>
                        )}
                        <div className="flex items-center gap-2">
                          <input
                            type="file" id={`pending-proof-${m.id}`} className="hidden"
                            accept="image/jpeg,image/png,image/webp,application/pdf" multiple
                            onChange={(e) => { uploadPendingProof(m, e.target.files); e.target.value = ''; }}
                            data-testid={`pending-proof-input-${m.id}`}
                          />
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            disabled={pendingUploadBusy === m.id}
                            onClick={() => document.getElementById(`pending-proof-${m.id}`)?.click()}
                            data-testid={`pending-proof-btn-${m.id}`}>
                            {pendingUploadBusy === m.id ? 'Uploading…' : (m.proof_urls || []).length ? 'Re-upload proof' : 'Upload proof'}
                          </Button>
                          {(m.ai_proof_check && m.proof_status !== 'rejected') && (
                            <span className="text-[10px] text-muted-foreground">AI pre-check: {m.ai_proof_check.verdict} · {m.ai_proof_check.score}/100</span>
                          )}
                        </div>
                      </div>
                    )}
                    {m.status === 'rejected' && (
                      <p className="text-xs text-rose-700 mt-1.5">
                        Reason: {m.rejection_reason || 'No reason given'}. You can re-apply below with corrected proof.
                      </p>
                    )}
                    {m.unit_request && (
                      <p className="text-xs text-amber-800 mt-1.5" data-testid={`unit-request-pending-${m.id}`}>
                        Additional flat(s) in review: {(m.unit_request.units || []).join(', ')}. You remain a verified member meanwhile.
                      </p>
                    )}
                    {m.unit_request_rejection && !m.unit_request && (
                      <p className="text-xs text-rose-700 mt-1.5">
                        Additional flat(s) {(m.unit_request_rejection.units || []).join(', ')} not approved: {m.unit_request_rejection.reason}
                      </p>
                    )}
                  </div>
                  {['verified', 'group_admin', 'pending'].includes(m.status) && (
                    <Button size="sm" variant="outline" className="shrink-0" data-testid={`add-flat-${m.id}`}
                      onClick={() => { resetWizard(); setProject({ id: m.project_id, name: m.project_name }); setAddingTo(m); setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add another flat
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Submitted success state */}
      {submitted && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-green-200 bg-green-50" data-testid="application-submitted">
            <CardContent className="py-6 text-center space-y-2">
              <CheckCircle className="h-10 w-10 mx-auto text-green-600" />
              <p className="font-semibold">{addingTo ? `Additional flat(s) submitted for ${project?.name}` : `Application submitted for ${project?.name}`}</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {addingTo && ['verified', 'group_admin'].includes(addingTo.status)
                  ? 'Our team will review the proof and add these flats to your verified membership. Your access is not affected meanwhile.'
                  : <>Admin review has started.{' '}
                    {submitted.fast_lane_pending
                      ? 'Your developer has also been emailed a one-click confirmation link — whichever lands first unlocks you.'
                      : 'You did not opt into the developer fast-lane, so admin review alone will decide.'}</>}
              </p>
              <Button variant="outline" size="sm" className="mt-2" onClick={resetWizard} data-testid="submit-another-btn">
                Add another flat or project
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Wizard */}
      {!submitted && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {step === 1 && 'Step 1 · Which project is your flat in?'}
              {step === 2 && (addingTo ? `Add another flat · ${project?.name}` : 'Step 2 · Units & proof of ownership')}
              {step === 3 && 'Step 3 · Review & submit'}
            </CardTitle>
            <CardDescription className="text-xs">
              {step === 1 && 'Search by project name, developer, or RERA number.'}
              {step === 2 && (addingTo
                ? `Already on your account: ${(addingTo.units || []).join(', ') || '—'}. Enter only the NEW flat number(s) and attach proof for them.`
                : 'Own more than one flat? Add each one below with "Add another unit". Attach proof: allotment letter, agreement first page, or registry.')}
              {step === 3 && 'Confirm everything is right before it goes to review.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* STEP 1 — project */}
            {step === 1 && (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="pl-10"
                    placeholder="Search projects…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="project-search-input"
                  />
                </div>
                {searchLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <div className="space-y-2">
                  {searchResults.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setProject(p); setAddingTo(memberships.find((m) => m.project_id === p.id && m.status !== 'rejected') || null); setStep(2); }}
                      className={`w-full text-left border rounded-lg p-3 hover:border-primary transition-colors ${
                        project?.id === p.id ? 'border-primary bg-primary/5' : ''
                      }`}
                      data-testid={`project-option-${p.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <p className="font-medium text-sm">{p.name}</p>
                        {p.rera_number && <Badge variant="outline" className="text-[10px]">{p.rera_number}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[p.developer_name, p.locality, p.city].filter(Boolean).join(' · ')}
                      </p>
                    </button>
                  ))}
                  {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      No projects match. If yours is missing, contact us to add it.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* STEP 2 — units + proof */}
            {step === 2 && project && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-md p-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  <span className="font-medium">{project.name}</span>
                  <button className="text-xs text-primary underline ml-auto" onClick={() => setStep(1)}>Change</button>
                </div>

                <div>
                  <Label>Your flat / unit number(s)</Label>
                  <div className="space-y-2 mt-1.5">
                    {units.map((u, i) => (
                      <div key={i} className="flex gap-2">
                        <Input
                          placeholder="e.g., Wing 3 · Flat 3203"
                          value={u}
                          onChange={(e) => updateUnit(i, e.target.value)}
                          data-testid={`unit-input-${i}`}
                        />
                        {units.length > 1 && (
                          <Button size="icon" variant="ghost" onClick={() => removeUnit(i)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={addUnit} data-testid="add-unit-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add another unit
                    </Button>
                    {!addingTo && (
                      <div className="pt-2">
                        <Label className="text-xs">Your name's position on the agreement</Label>
                        <select value={holderPos} onChange={(e) => setHolderPos(e.target.value)} data-testid="holder-position-select"
                          className="mt-1 block w-full sm:w-72 rounded-md border bg-background px-3 py-2 text-sm">
                          {HOLDER_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <p className="text-[11px] text-muted-foreground mt-1">Joint owners each verify with their own login; the directory lists them in this order.</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label>Proof of ownership <span className="text-muted-foreground font-normal">(optional — upload later if not handy)</span></Label>
                  <div
                    className="mt-1.5 border-2 border-dashed rounded-lg p-5 text-center cursor-pointer hover:bg-muted/40 transition-colors"
                    onClick={() => fileRef.current?.click()}
                    data-testid="proof-upload-zone"
                  >
                    <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm font-medium">Upload proof documents</p>
                    <p className="text-xs text-muted-foreground">
                      Allotment letter, agreement first page, or registry — JPG/PNG/WebP/PDF, up to 10 MB each. Multi-select supported.
                    </p>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      multiple
                      className="hidden"
                      data-testid="proof-file-input"
                      onChange={(e) => uploadProof(e.target.files)}
                    />
                  </div>
                  {proofFiles.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {proofFiles.map((p, i) => (
                        <div key={i} className="flex items-center gap-2 border rounded-md p-2 text-sm">
                          {p.uploading ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : (
                            <FileText className="h-4 w-4 text-green-600" />
                          )}
                          <span className="flex-1 truncate">{p.name}</span>
                          <Button size="icon" variant="ghost" onClick={() => removeProof(i)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Label>Phone (for verification call if needed)</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" data-testid="verify-phone-input" />
                </div>

                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <Checkbox
                    id="fast-lane"
                    checked={fastLane}
                    onCheckedChange={(v) => setFastLane(!!v)}
                    className="mt-0.5"
                    data-testid="fast-lane-checkbox"
                  />
                  <label htmlFor="fast-lane" className="text-sm">
                    <span className="font-medium flex items-center gap-1">
                      <Zap className="h-3.5 w-3.5 text-amber-600" /> Enable developer fast-lane
                    </span>
                    <span className="text-xs text-muted-foreground block mt-0.5">
                      We email your developer a one-click "Yes, this person owns a unit" link. Their confirm approves
                      you instantly — no need to wait for admin review. Admin review runs in parallel either way.
                    </span>
                  </label>
                </div>

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                  <Button onClick={() => setStep(3)} disabled={!canProceedStep2} data-testid="to-review-btn">
                    Review <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3 — review */}
            {step === 3 && project && (
              <div className="space-y-4">
                <div className="border rounded-lg divide-y text-sm">
                  <div className="p-3 flex justify-between"><span className="text-muted-foreground">Project</span><span className="font-medium">{project.name}</span></div>
                  <div className="p-3 flex justify-between"><span className="text-muted-foreground">Units</span><span className="font-medium">{cleanUnits.join(', ')}</span></div>
                  {!addingTo && <div className="p-3 flex justify-between"><span className="text-muted-foreground">Your position</span><span className="font-medium" data-testid="review-holder-position">{HOLDER_SHORT[holderPos]}</span></div>}
                  <div className="p-3 flex justify-between"><span className="text-muted-foreground">Proof documents</span><span className="font-medium">{cleanProofs.length} attached</span></div>
                  <div className="p-3 flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium">{phone || '—'}</span></div>
                  <div className="p-3 flex justify-between"><span className="text-muted-foreground">Developer fast-lane</span><span className="font-medium">{fastLane ? 'Enabled' : 'Off'}</span></div>
                </div>
                <div>
                  <Label>Anything we should know? (optional)</Label>
                  <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" data-testid="verify-notes-input" />
                </div>
                <p className="text-xs text-muted-foreground">
                  By submitting you confirm the proof documents are genuine. False submissions lead to permanent removal.
                </p>
                <div className="flex justify-between pt-1">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                  </Button>
                  <Button onClick={submit} disabled={submitting} data-testid="submit-verification-btn">
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
                    ) : (
                      <><Home className="h-4 w-4 mr-1" /> Submit for verification</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
